import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Button } from "../components/Buttons";
import {
  getMaintenanceConfig,
  setMaintenanceConfig,
  getOtpChannels,
  setOtpChannels,
} from "../api";

/** "22:00" → "10:00 PM", for the plain-English summary line. */
const to12h = (hhmm) => {
  const [h, m] = String(hhmm || "").split(":").map(Number);
  if (Number.isNaN(h)) return hhmm;
  const suffix = h < 12 ? "AM" : "PM";
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, "0")} ${suffix}`;
};

/**
 * Admin control for (a) the daily downtime window during which the site is
 * locked for everyone but the admin, and (b) which OTP channels the sign-in
 * page offers.
 */
export default function AdminSiteTiming() {
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);

  /* Maintenance window */
  const [enabled, setEnabled] = useState(false);
  const [start, setStart] = useState("00:00");
  const [end, setEnd] = useState("00:00");
  const [message, setMessage] = useState("");
  const [savingWindow, setSavingWindow] = useState(false);

  /* OTP channels */
  const [sms, setSms] = useState(true);
  const [email, setEmail] = useState(true);
  const [savingOtp, setSavingOtp] = useState(false);

  useEffect(() => {
    Promise.all([
      getMaintenanceConfig()
        .then(({ data }) => {
          setEnabled(!!data.enabled);
          setStart(data.start || "00:00");
          setEnd(data.end || "00:00");
          setMessage(data.message || "");
        })
        .catch(() => {}),
      getOtpChannels()
        .then(({ data }) => {
          setSms(!!data.sms);
          setEmail(!!data.email);
        })
        .catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  async function handleSaveWindow() {
    setSavingWindow(true);
    setFeedback(null);
    try {
      const { data } = await setMaintenanceConfig({
        enabled,
        start,
        end,
        message,
      });
      setEnabled(!!data.enabled);
      setStart(data.start);
      setEnd(data.end);
      setMessage(data.message);
      setFeedback({ severity: "success", msg: "Site timing saved." });
    } catch (e) {
      setFeedback({
        severity: "error",
        msg: e.response?.data?.error || "Could not save",
      });
    } finally {
      setSavingWindow(false);
    }
  }

  async function handleSaveOtp(next) {
    setSavingOtp(true);
    setFeedback(null);
    try {
      const { data } = await setOtpChannels(next);
      setSms(!!data.sms);
      setEmail(!!data.email);
      setFeedback({ severity: "success", msg: "OTP channels saved." });
    } catch (e) {
      /* Rejected (e.g. both switched off) — snap the toggles back. */
      setFeedback({
        severity: "error",
        msg: e.response?.data?.error || "Could not save",
      });
      const { data } = await getOtpChannels().catch(() => ({ data: {} }));
      setSms(!!data.sms);
      setEmail(!!data.email);
    } finally {
      setSavingOtp(false);
    }
  }

  const wrapsMidnight = enabled && start > end && start !== end;

  if (loading) return <Typography sx={{ p: 3 }}>Loading…</Typography>;

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 0.5 }}>
        Site Timing & Login
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Take the site down on a daily schedule, and choose which OTP channels
        the sign-in page offers.
      </Typography>

      {feedback && (
        <Alert
          severity={feedback.severity}
          sx={{ mb: 2 }}
          onClose={() => setFeedback(null)}
        >
          {feedback.msg}
        </Alert>
      )}

      {/* ---------------- Downtime window ---------------- */}
      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
            Daily Downtime Window
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Inside this window the whole site is locked: no one can log in and
            signed-in users are shown a maintenance notice. <b>Admin is always
            exempt</b>, so you can still get in and switch it off. Payment
            gateway callbacks keep running, so in-flight payins and payouts
            still settle. All times are IST, 24-hour.
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
            }
            label={enabled ? "Downtime schedule is ON" : "Downtime schedule is OFF"}
            sx={{ mb: 2 }}
          />

          <Stack direction="row" gap={2} flexWrap="wrap" sx={{ mb: 2 }}>
            <TextField
              type="time"
              size="small"
              label="Site goes down at"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
              sx={{ maxWidth: 200 }}
            />
            <TextField
              type="time"
              size="small"
              label="Site comes back at"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
              sx={{ maxWidth: 200 }}
            />
          </Stack>

          {enabled && start !== end && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Site will be <b>down from {to12h(start)} to {to12h(end)}</b> every
              day
              {wrapsMidnight && (
                <Chip
                  size="small"
                  label="crosses midnight"
                  sx={{ ml: 1 }}
                  color="warning"
                />
              )}
              .
            </Alert>
          )}

          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            label="Message shown to users"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="The site is temporarily down for maintenance."
            sx={{ mb: 2 }}
          />

          <Button
            variant="contained"
            color="primary"
            loading={savingWindow}
            onClick={handleSaveWindow}
          >
            Save Site Timing
          </Button>
        </CardContent>
      </Card>

      {/* ---------------- OTP channels ---------------- */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
            Login OTP Channels
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Which channels users can receive a login OTP on. Turn on one or
            both — at least one must stay on, since OTP is the only way a user
            who forgot their password and MPIN can get back in.
          </Typography>

          <Stack sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={sms}
                  disabled={savingOtp}
                  onChange={(e) => {
                    setSms(e.target.checked);
                    handleSaveOtp({ sms: e.target.checked, email });
                  }}
                />
              }
              label="SMS OTP (mobile number)"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={email}
                  disabled={savingOtp}
                  onChange={(e) => {
                    setEmail(e.target.checked);
                    handleSaveOtp({ sms, email: e.target.checked });
                  }}
                />
              }
              label="Email OTP (email address)"
            />
          </Stack>

          <Typography variant="body2" color="text.secondary">
            {sms && email
              ? "Sign-in page shows both Phone and Email."
              : sms
                ? "Sign-in page shows Phone only."
                : "Sign-in page shows Email only."}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
